import { createContext, FC, useContext, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LoadingOutlined } from '@ant-design/icons';
import { getCHVisitReportDetail } from '@/libs/fleet-api/services/visit-reports';
import MainInformation from '@/components/MainInformation';
import { Divider, Tabs } from 'antd';
import FleetTitle from '@/libs/antd/components/Title';
import FBDHVisitReportApprovalSite from './VisitReportApproval.Site';
import FBDHVisitReportApprovalOffice from './VisitReportApproval.Office';
import FBDHVisitReportApprovalForm from './VisitReportApproval.Approval';
import GenericErrorMessage from '@/components/GenericErrorMessage';

const useGetFBDHVisitReportDetail = (prospectId: string) => {
	return useQuery({
		queryKey: ['getCHVisitReportDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getCHVisitReportDetail(prospectId);
		},
	});
};

const context = createContext<
	ReturnType<typeof useGetFBDHVisitReportDetail>['data'] | null
>(null);

const { Provider } = context;

export const useFBDHVisitReportApprovalPage = () => {
	const { data } = useContext(context)!;
	return data;
};

const FBDHVisitReportApproval: FC<{ prospectId: string }> = ({
	prospectId,
}) => {
	const { data, isLoading, isFetching, isError } =
		useGetFBDHVisitReportDetail(prospectId);
	const {
		mainInformation,
		surveyInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
	} = data?.data ?? {};

	const [activeTabKey, setActiveTabKey] = useState<string>('office');

	if (isError) return <GenericErrorMessage />;
	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Provider value={data}>
			<MainInformation
				mainInformation={mainInformation}
				surveyInformation={surveyInformation}
				customerBusinessDocuments={customerBusinessDocuments}
				customerLegalDocuments={customerLegalDocuments}
				customerReferralDocuments={customerReferralDocuments}
				isLoading={isLoading}
			/>
			<Divider />
			<FleetTitle>Approval Laporan Kunjungan</FleetTitle>
			<Tabs
				activeKey={activeTabKey}
				onChange={(key) => setActiveTabKey(key)}
				items={[
					{
						key: 'office',
						label: 'Laporan Kunjungan Kantor',
						children: (
							<FBDHVisitReportApprovalOffice
								onNext={() => {
									setActiveTabKey('site');
								}}
							/>
						),
					},
					{
						key: 'site',
						label: 'Laporan Kunjungan Site',
						children: (
							<FBDHVisitReportApprovalSite
								onNext={() => {
									setActiveTabKey('approval');
								}}
							/>
						),
					},
					{
						key: 'call',
						label: 'Call Report',
						children: 'WIP',
					},
					{
						key: 'approval',
						label: 'Approval',
						children: <FBDHVisitReportApprovalForm />,
					},
				]}
			/>
		</Provider>
	);
};

export default FBDHVisitReportApproval;
